import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Terminal, BadgeCheck, ArrowUpRight, Filter, LogOut, Sparkles } from "lucide-react";
import BrandMark from "@/components/BrandMark";
import LatestScroll from "@/components/LatestScroll";
import CASES, { casesByIndustryKey } from "@/data/caseStudies";
import { INDUSTRIES, industryLabel } from "@/data/taxonomy";

const memberFromStorage = () => {
  try {
    return JSON.parse(localStorage.getItem("deployd_member") || "null");
  } catch {
    return null;
  }
};

export default function Feed() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [member, setMember] = useState(memberFromStorage);
  const [industry, setIndustry] = useState(params.get("industry") || member?.industry || "");

  useEffect(() => {
    if (!member) {
      navigate("/", { replace: true });
    }
  }, [member, navigate]);

  useEffect(() => {
    const next = new URLSearchParams(params);
    if (industry) next.set("industry", industry);
    else next.delete("industry");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [industry]);

  const list = useMemo(() => {
    const base = industry ? casesByIndustryKey(industry) : CASES;
    return base;
  }, [industry]);

  const logout = () => {
    localStorage.removeItem("deployd_member");
    setMember(null);
    navigate("/", { replace: true });
  };

  return (
    <main
      data-testid="feed-page"
      className="relative min-h-screen w-full bg-[#F7F6F1] text-zinc-900"
    >
      <div className="grain-overlay" />

      <header className="relative z-10 flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200 bg-white/60 px-6 py-4 backdrop-blur sm:px-10 lg:px-16">
        <Link
          to="/"
          className="flex items-center gap-3 font-mono-tech text-[11px] uppercase tracking-[0.22em] text-zinc-600 hover:text-emerald-700"
        >
          <Terminal className="h-3.5 w-3.5 text-emerald-600" />
          <BrandMark size="inline" /> / feed
        </Link>
        <div className="flex items-center gap-3">
          {member && (
            <span className="hidden font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-500 sm:inline">
              {member.full_name || member.email}
            </span>
          )}
          <Link
            to="/dashboard"
            data-testid="feed-dashboard-link"
            className="inline-flex items-center gap-2 border border-zinc-300 bg-white px-3 py-2 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-700 hover:border-emerald-500 hover:text-emerald-700"
          >
            <Sparkles className="h-3.5 w-3.5" />
            My dashboard
          </Link>
          <button
            onClick={logout}
            data-testid="feed-logout"
            className="inline-flex items-center gap-2 border border-zinc-300 bg-white px-3 py-2 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-700 hover:border-red-400 hover:text-red-600"
          >
            <LogOut className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
      </header>

      <section className="relative z-10 mx-auto w-full max-w-6xl px-6 pb-16 pt-10 sm:px-10 sm:pt-14 lg:px-16">
        <p className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-emerald-600">
          // your industry feed
        </p>
        <h1 className="mt-3 font-display text-4xl font-extrabold leading-[0.98] tracking-tight text-zinc-900 sm:text-5xl">
          What&apos;s actually being deployed{" "}
          <span className="text-emerald-600">around you.</span>
        </h1>
        <p className="mt-4 max-w-2xl text-zinc-600 sm:text-lg">
          Verified use cases from your industry, updated as we onboard new customer stories.
        </p>

        {/* Filter */}
        <div className="mt-8 flex flex-wrap items-center gap-3 border-y border-zinc-200 py-3">
          <span className="inline-flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-[0.24em] text-zinc-500">
            <Filter className="h-3.5 w-3.5" /> Industry
          </span>
          <select
            data-testid="feed-industry-filter"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            className="border border-zinc-300 bg-white px-3 py-2 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-800 outline-none focus:border-emerald-500"
          >
            <option value="">All industries</option>
            {INDUSTRIES.map((i) => (
              <option key={i.value} value={i.value}>
                {i.label}
              </option>
            ))}
          </select>
          <span className="ml-auto font-mono-tech text-[10px] uppercase tracking-[0.24em] text-zinc-500">
            {list.length} verified {list.length === 1 ? "case" : "cases"}
          </span>
        </div>

        {/* Grid */}
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((c) => (
            <FeedCard key={c.id} item={c} />
          ))}
          {list.length === 0 && (
            <div className="col-span-full border border-dashed border-zinc-300 bg-white/60 p-10 text-center">
              <p className="font-mono-tech text-[11px] uppercase tracking-[0.24em] text-zinc-500">
                No verified cases for {industryLabel(industry)} yet.
              </p>
              <p className="mt-2 text-sm text-zinc-600">
                We&apos;re actively onboarding. Meanwhile explore adjacent industries or{" "}
                <Link to="/dashboard" className="text-emerald-700 underline">
                  suggest a case study
                </Link>
                .
              </p>
            </div>
          )}
        </div>
      </section>

      <LatestScroll title="Latest use cases" />

      <footer className="relative z-10 border-t border-zinc-200 px-6 py-6 sm:px-10 lg:px-16">
        <div className="mx-auto flex w-full max-w-6xl flex-col items-start justify-between gap-3 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-500 sm:flex-row sm:items-center">
          <span>
            © {new Date().getFullYear()} <BrandMark size="inline" />, Inc.
          </span>
          <span className="inline-flex items-center gap-2">
            <BadgeCheck className="h-3 w-3 text-emerald-600" /> Verified use cases only
          </span>
        </div>
      </footer>
    </main>
  );
}

const FeedCard = ({ item }) => (
  <Link
    to={`/case-study/${item.id}`}
    data-testid={`feed-${item.id}`}
    className="group flex flex-col gap-3 border border-zinc-200 bg-white p-5 no-underline transition-colors hover:border-emerald-500 hover:shadow-[0_1px_0_0_rgba(10,10,10,0.04),0_24px_60px_-30px_rgba(10,10,10,0.18)]"
  >
    <div className="flex items-center justify-between">
      <span className="inline-flex items-center gap-1 border border-emerald-500/50 bg-emerald-50 px-2 py-1 font-mono-tech text-[9px] uppercase tracking-[0.22em] text-emerald-700">
        <BadgeCheck className="h-3 w-3" />
        Verified
      </span>
      <ArrowUpRight className="h-4 w-4 text-zinc-300 transition-colors group-hover:text-emerald-600" />
    </div>

    {/* Animated bars placeholder (GIF-like) */}
    <div className="mt-1 flex h-20 items-end justify-between gap-1 rounded bg-emerald-50 p-3">
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
        <span
          key={i}
          className="chart-bar w-2 rounded-sm bg-emerald-600/80"
          style={{ animationDelay: `${i * 0.18}s` }}
        />
      ))}
    </div>

    <div>
      <p className="font-mono-tech text-[10px] uppercase tracking-[0.24em] text-zinc-500">
        {item.vendor} <span className="text-zinc-300">·</span> at
      </p>
      <p className="mt-1 font-display text-xl font-extrabold leading-tight tracking-tight text-zinc-900 group-hover:text-emerald-700">
        {item.customer}
      </p>
    </div>

    <div className="flex flex-wrap gap-2 font-mono-tech text-[9px] uppercase tracking-[0.22em]">
      <span className="border border-zinc-300 bg-white px-2 py-1 text-zinc-700">{item.industry}</span>
      <span className="border border-zinc-300 bg-white px-2 py-1 text-zinc-700">{item.size}</span>
    </div>

    <p className="mt-1 text-sm leading-relaxed text-zinc-700">{item.summary}</p>
    <p className="mt-auto font-mono-tech text-[10px] uppercase tracking-[0.24em] text-emerald-700">
      {item.metric} &middot; {item.metricLabel}
    </p>
  </Link>
);
